/**
 * Nicknames Tab Component
 * Admin interface for managing nickname word lists (adjectives, colours, creatures)
 */
import { useState } from "react";
import { Sparkles, Save, Edit2, X } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";

const API = '/api';

export default function NicknamesTab({ nicknameLists, onRefresh }) {
  const [editingListType, setEditingListType] = useState(null);
  const [editingListText, setEditingListText] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const handleStartEditing = (listType) => {
    const words = nicknameLists[listType] || [];
    setEditingListType(listType);
    setEditingListText(words.join('\n'));
  };

  const handleCancelEditing = () => {
    setEditingListType(null);
    setEditingListText("");
  };

  const handleSaveList = async (listType) => {
    const words = editingListText.split('\n').map(w => w.trim()).filter(w => w);
    
    if (words.length === 0) {
      toast.error("Please add at least one word");
      return;
    }
    
    setIsSaving(true);
    
    try {
      const response = await fetch(`${API}/admin/nickname-lists/${listType}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ words })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`${listType.charAt(0).toUpperCase() + listType.slice(1)} updated! (${data.count} words)`);
        setEditingListType(null);
        setEditingListText("");
        onRefresh();
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || "Failed to update - please try again");
      }
    } catch (e) {
      console.error("Error saving nickname list:", e);
      toast.error("Error saving - please check your connection");
    } finally {
      setIsSaving(false);
    }
  };

  const renderListEditor = (listType, title) => {
    const words = nicknameLists[listType] || [];
    const isEditing = editingListType === listType;

    return (
      <div key={listType} className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-medium">{title}</h3>
          <Badge variant="outline">{words.length}</Badge>
        </div>
        
        {isEditing ? (
          <div className="space-y-2">
            <Textarea
              value={editingListText}
              onChange={(e) => setEditingListText(e.target.value)}
              placeholder="One word per line..."
              className="min-h-[200px] text-sm font-mono"
              autoFocus
            />
            <div className="flex gap-2">
              <Button 
                size="sm" 
                onClick={() => handleSaveList(listType)}
                disabled={isSaving}
              >
                <Save className="w-4 h-4 mr-1" /> 
                {isSaving ? "Saving..." : "Save"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleCancelEditing}
                disabled={isSaving}
              >
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {editingListText.split('\n').filter(w => w.trim()).length} words
            </p>
          </div>
        ) : (
          <>
            <div className="bg-muted/50 rounded-lg p-3 max-h-[200px] overflow-y-auto text-sm">
              {words.slice(0, 15).join(", ")}
              {words.length > 15 && "..."}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleStartEditing(listType)}
            >
              <Edit2 className="w-4 h-4 mr-1" /> Edit
            </Button>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="bg-card rounded-2xl border border-border/40 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-xl bg-secondary/10">
          <Sparkles className="w-5 h-5 text-secondary" />
        </div>
        <div>
          <h2 className="font-heading font-semibold text-lg">Nickname Generator Lists</h2>
          <p className="text-sm text-muted-foreground">
            Manage adjectives, colours, and creatures used for random nicknames
          </p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {renderListEditor("adjectives", "Adjectives")}
        {renderListEditor("colours", "Colours")}
        {renderListEditor("creatures", "Creatures")}
      </div>

      <div className="mt-6 p-4 bg-muted/30 rounded-lg">
        <p className="text-sm text-muted-foreground">
          <strong>Preview:</strong> Nicknames are generated as <code className="bg-muted px-1 rounded">Adjective-Colour-Creature</code> 
          {" "}(e.g., "{nicknameLists.adjectives?.[0] || 'Swift'}-{nicknameLists.colours?.[0] || 'Blue'}-{nicknameLists.creatures?.[0] || 'Dragon'}")
        </p>
      </div>
    </div>
  );
}
